import {
    Button,
    Card,
    CardContent,
    CardHeader,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import Loading from "components/Loading";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "providers/Notification";
import { PatternList } from "components/PatternList";
import ConfigurationOption from "components/ConfigurationOption";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";

interface ApplicationPattern {
    pattern: string;
    appId: string;
    appName: string;
    filterType: string;
    comment: string;
}

interface BypassConfig {
    mode: "scan_by_default" | "bypass_by_default";
    bypassUrlPatterns: string[];
    scanUrlPatterns: string[];
    includeApplications: boolean;
    applicationPatterns: ApplicationPattern[];
}

function ScanningMode() {
    const notification = useNotification();
    const login = useLogin();
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [mode, setMode] = useState<"scan_by_default" | "bypass_by_default">("scan_by_default");
    const [bypassUrlPatterns, setbypassUrlPatterns] = useState<string[]>([]);
    const [scanUrlPatterns, setscanUrlPatterns] = useState<string[]>([]);
    const [includeApplications, setIncludeApplications] = useState(false);
    const [applicationPatterns, setApplicationPatterns] = useState<ApplicationPattern[]>([]);

    const req = login.GetAxios();

    const loadData = async () => {
        setFinishedLoading(false);
        if (!req) {
            return;
        }
        try {
            const resp = await req.get("/api/config/bypass");
            const config: BypassConfig = resp.data;

            setMode(config.mode);
            setbypassUrlPatterns([...(config.bypassUrlPatterns || [])].sort());
            setscanUrlPatterns([...(config.scanUrlPatterns || [])].sort());
            setIncludeApplications(config.includeApplications || false);
            setApplicationPatterns(config.applicationPatterns || []);
            setFinishedLoading(true);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            notification.Display({
                title: "Error",
                message: "Error fetching scanning mode configuration",
                type: "error",
            });
        }
    };

    const loadApplicationPatterns = async () => {
        if (!req) {
            return;
        }
        try {
            const resp = await req.get("/api/config/bypass/application-patterns");
            setApplicationPatterns(resp.data || []);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            notification.Display({
                title: "Error",
                message: "Error fetching application patterns",
                type: "error",
            });
        }
    };

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load application patterns when mode is bypass_by_default and includeApplications is true
    useEffect(() => {
        if (mode === "bypass_by_default" && includeApplications) {
            loadApplicationPatterns();
        } else {
            setApplicationPatterns([]);
        }
    }, [mode, includeApplications]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveConfiguration = async () => {
        if (!req) {
            return;
        }

        setSaving(true);

        try {
            await req.put("/api/config/bypass", {
                mode,
                bypassUrlPatterns,
                scanUrlPatterns,
                includeApplications,
            });
            notification.Display({
                type: "success",
                title: "Success",
                message: "Scanning mode configuration saved successfully",
            });
            await loadData();
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            notification.Display({
                title: "Error",
                message: "Error saving scanning mode configuration",
                type: "error",
            });
        }

        setSaving(false);
    };

    const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split("\n")
                .map(line => line.trim())
                .filter(line => line.length > 0 && !line.startsWith("#"));

            if (mode === "scan_by_default") {
                const newPatterns = [...new Set([...bypassUrlPatterns, ...lines])].sort();
                setbypassUrlPatterns(newPatterns);
            } else {
                const newPatterns = [...new Set([...scanUrlPatterns, ...lines])].sort();
                setscanUrlPatterns(newPatterns);
            }

            notification.Display({
                type: "success",
                title: "Success",
                message: `Imported ${lines.length} patterns`,
            });
        };
        reader.readAsText(file);
        event.target.value = "";
    };

    const handleDownload = () => {
        const patterns = mode === "scan_by_default" ? bypassUrlPatterns : scanUrlPatterns;
        const blob = new Blob([patterns.join("\n")], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = mode === "scan_by_default" ? "bypass-patterns.txt" : "scan-patterns.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return <Loading finished={finishedLoading}>
        <Typography variant="h1">Scanning Mode</Typography>

        <Box display="flex" flexDirection="column">
            <Card sx={{ marginTop: "30px", padding: "0px" }}>
                <CardHeader
                    component={Typography}
                    titleTypographyProps={{ variant: "h3" }}
                    title={"Scanning Mode Configuration"}
                    subheaderTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    subheader={"Configure which URLs should be scanned or bypassed by the privacy proxy"}
                />
                <CardContent sx={{ padding: "10px 25px" }}>
                    <FormControl>
                        <RadioGroup
                            value={mode}
                            onChange={(e) => setMode(e.target.value as "scan_by_default" | "bypass_by_default")}
                        >
                            <FormControlLabel
                                value="scan_by_default"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="h4">Scan all traffic by default</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            All URLs will be scanned except those matching bypass patterns
                                        </Typography>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="bypass_by_default"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="h4">Bypass all traffic by default</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            All URLs will bypass scanning except those matching scan patterns
                                        </Typography>
                                    </Box>
                                }
                                sx={{ marginTop: "10px" }}
                            />
                        </RadioGroup>
                    </FormControl>

                    {mode === "bypass_by_default" && (
                        <Box sx={{ marginTop: "20px", marginLeft: "32px" }}>
                            <ConfigurationOption
                                id="include-applications"
                                label="Include Application URL filters as scan patterns"
                                description="When enabled, URL filters defined in Applications will be automatically scanned"
                                checked={includeApplications}
                                onChange={setIncludeApplications}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: "30px", padding: "0px" }}>
                <CardHeader
                    component={Typography}
                    titleTypographyProps={{ variant: "h3" }}
                    title={mode === "scan_by_default" ? "Bypass URL Patterns" : "Scan URL Patterns"}
                    subheaderTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    subheader={
                        mode === "scan_by_default"
                            ? "URLs matching these patterns will bypass scanning and be passed through unchanged"
                            : "URLs matching these patterns will be scanned for sensitive data"
                    }
                />
                <CardContent sx={{ padding: "10px 25px" }}>
                    <Box display="flex" gap="10px" alignItems="center" sx={{ mb: 3 }}>
                        <input
                            accept=".txt"
                            style={{ display: "none" }}
                            id="bulk-upload-button"
                            type="file"
                            onChange={handleBulkUpload}
                        />
                        <label htmlFor="bulk-upload-button">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<UploadIcon />}
                            >
                                Bulk Upload
                            </Button>
                        </label>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            disabled={
                                mode === "scan_by_default"
                                    ? bypassUrlPatterns.length === 0
                                    : scanUrlPatterns.length === 0
                            }
                        >
                            Download List
                        </Button>
                    </Box>

                    <PatternList
                        patterns={[
                            // User-defined patterns (editable)
                            ...(mode === "scan_by_default" ? bypassUrlPatterns : scanUrlPatterns).map(p => ({
                                value: p
                            })),
                            // Application patterns (read-only, shown only in bypass mode with includeApplications)
                            ...(mode === "bypass_by_default" && includeApplications
                                ? applicationPatterns.map(ap => ({
                                    value: ap.pattern,
                                    source: {
                                        appId: ap.appId,
                                        appName: ap.appName,
                                        filterType: ap.filterType,
                                        comment: ap.comment
                                    }
                                }))
                                : []
                            )
                        ].sort((a, b) => a.value.localeCompare(b.value))}
                        onAdd={(pattern) => {
                            if (mode === "scan_by_default") {
                                if (!bypassUrlPatterns.includes(pattern)) {
                                    setbypassUrlPatterns([...bypassUrlPatterns, pattern].sort());
                                }
                            } else {
                                if (!scanUrlPatterns.includes(pattern)) {
                                    setscanUrlPatterns([...scanUrlPatterns, pattern].sort());
                                }
                            }
                        }}
                        onDelete={(pattern) => {
                            if (mode === "scan_by_default") {
                                setbypassUrlPatterns(bypassUrlPatterns.filter(p => p !== pattern));
                            } else {
                                setscanUrlPatterns(scanUrlPatterns.filter(p => p !== pattern));
                            }
                        }}
                        placeholder="Enter a URL pattern (e.g., ^https://example\.com/.*)"
                        label="URL Pattern (regex)"
                        allowAdd={true}
                    />
                </CardContent>
            </Card>
        </Box>

        <Box display="flex" sx={{ marginTop: "50px", marginBottom: "50px" }}>
            <LoadingButton
                id="save-scanning-mode-button"
                variant="contained"
                sx={{ marginLeft: "auto" }}
                onClick={saveConfiguration}
                loading={saving}
            >
                Save Changes
            </LoadingButton>
        </Box>
    </Loading>;
}

export default ScanningMode;
